/**
 * Which of the images ArgoCD reports for an environment is the project's own.
 *
 * ArgoCD lists every image of the Application's live resources, sorted
 * alphabetically: on an Odoo branch, the init container's busybox, the CNPG
 * operator's, postgres', and Odoo's — usually last. The band shows the
 * project's image first and folds the others away, from data the page already
 * holds (the project and the repository URL of the Application); no request,
 * and no registry host written in the code.
 *
 * Three tiers, the order within a tier being ArgoCD's:
 *  0. THE PROJECT'S: the image repository path, host removed, is the forge
 *     project's path or under it (GitLab's container registry names images
 *     after the project path: `<registry>/<group>/<project>[/<name>]`), or its
 *     last segment is the project id;
 *  1. FROM THE SAME ORGANISATION: the registry host and the forge host share
 *     their parent domain (`registry.example.org` and `gitlab.example.org`);
 *  2. everything else — public base images.
 * These are heuristics on names, documented as such: the worst they can do is
 * show a less interesting image first, never hide one.
 */

/** An image reference split into what the tiers compare. */
interface ParsedImage {
  /** The registry host, when the reference names one. */
  host?: string;
  /** The repository path, without host, tag or digest. */
  path: string;
}

/**
 * `host/path:tag@digest` split the way Docker reads it: the first segment is a
 * host only if it carries a dot or a port, or is `localhost`.
 */
export function parseImage(reference: string): ParsedImage {
  const withoutDigest = reference.split('@')[0];
  const lastSlash = withoutDigest.lastIndexOf('/');
  const lastColon = withoutDigest.lastIndexOf(':');
  const withoutTag =
    lastColon > lastSlash ? withoutDigest.slice(0, lastColon) : withoutDigest;

  const [first, ...rest] = withoutTag.split('/');
  const isHost =
    rest.length > 0 &&
    (first.includes('.') || first.includes(':') || first === 'localhost');
  return isHost
    ? { host: first.toLowerCase(), path: rest.join('/').toLowerCase() }
    : { path: withoutTag.toLowerCase() };
}

/** `gitlab.example.org` → `example.org`; undefined for a single-label host. */
function parentDomain(host: string): string | undefined {
  const hostname = host.split(':')[0];
  const dot = hostname.indexOf('.');
  const parent = dot === -1 ? '' : hostname.slice(dot + 1);
  return parent.includes('.') ? parent : undefined;
}

export interface RankedImages {
  /**
   * Shown in the band: the project's images (tier 0); failing that, the first
   * image of the organisation's registry (tier 1); failing that, none — a
   * public base image is not "the" image of the environment, and putting the
   * first one ArgoCD lists in front would only look meaningful.
   */
  primary: string[];
  /** Folded behind a "+N" control. */
  others: string[];
}

export function rankImages(
  images: string[],
  context: { project: string; repoUrl?: string },
): RankedImages {
  if (images.length === 0) {
    return { primary: [], others: [] };
  }

  let repoPath: string | undefined;
  let repoParent: string | undefined;
  if (context.repoUrl) {
    try {
      const url = new URL(context.repoUrl);
      repoPath = url.pathname.replace(/^\/+|\/+$/g, '').toLowerCase();
      repoParent = parentDomain(url.host.toLowerCase());
    } catch {
      // An unparseable repository URL only costs tiers 0 (by path) and 1.
    }
  }
  const project = context.project.toLowerCase();

  const tier = (image: string): number => {
    const { host, path } = parseImage(image);
    const lastSegment = path.slice(path.lastIndexOf('/') + 1);
    if (
      (repoPath && (path === repoPath || path.startsWith(`${repoPath}/`))) ||
      lastSegment === project
    ) {
      return 0;
    }
    if (host && repoParent && parentDomain(host) === repoParent) {
      return 1;
    }
    return 2;
  };

  // A stable sort by tier: Array.prototype.sort is stable since ES2019.
  const ranked = images
    .map((image, index) => ({ image, index, tier: tier(image) }))
    .sort((a, b) => a.tier - b.tier || a.index - b.index);

  const projectImages = ranked.filter(item => item.tier === 0);
  let primaryCount = 0;
  if (projectImages.length > 0) {
    primaryCount = projectImages.length;
  } else if (ranked[0].tier === 1) {
    primaryCount = 1;
  }
  return {
    primary: ranked.slice(0, primaryCount).map(item => item.image),
    others: ranked.slice(primaryCount).map(item => item.image),
  };
}

/**
 * An image reference as the band shows it: the repository's last path segment
 * and its tag — `registry.example.org/waadoo/sandboxes/ludo/app1:4f2c9e1`
 * becomes `app1:4f2c9e1`. Case is kept: this is a label, not a comparison.
 *
 * A reference pinned by digest alone keeps a short digest instead — the
 * algorithm and the first 12 characters of the hash, the length Docker
 * abbreviates image ids to — so that two builds of the same repository can
 * still be told apart. When it carries a tag too, the tag is what a person
 * recognises and the digest is dropped.
 */
export function shortImageName(reference: string): string {
  const [named, digest] = reference.split('@');
  // The tag, if any, follows the last slash: a colon before it is a port.
  const name = named.slice(named.lastIndexOf('/') + 1);
  if (!digest || name.includes(':')) {
    return name;
  }
  // `sha256:` and the 12 characters after it.
  return `${name}@${digest.slice(0, digest.indexOf(':') + 13)}`;
}

/**
 * The tag of an image reference — `7.4-hb5ed41a6e103` of
 * `registry.example.org/group/shop:7.4-hb5ed41a6e103@sha256:…` —; undefined
 * for a reference without one (by digest alone, or the implicit `latest`).
 */
export function imageTag(reference: string): string | undefined {
  const named = reference.split('@')[0];
  // The tag follows the last slash: a colon before it is a port.
  const name = named.slice(named.lastIndexOf('/') + 1);
  const colon = name.indexOf(':');
  return colon === -1 || colon === name.length - 1
    ? undefined
    : name.slice(colon + 1);
}
